/**
 * End of Life edit view — story 37058.
 * Structural base: packaging-pcf/material-emission-factors/material-emission.
 */

import { FC, useCallback, useEffect, useMemo, useState, EffectCallback } from 'react'
import get from 'lodash/get'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, LottieLoader, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { fetchEndOfLife, getConfiguration } from './endOfLifeActions'
import { showCorrelationIdError } from '../../redux/correlationIdSlice'
import { handlePublishStatus, setDefaultEndOfLife } from '../../redux/endOfLifeSlice'
import { createEndOfLife, updateEndOfLife, publishEndOfLife, unPublishEndOfLife } from './api'
import {
  blockerTab,
  formSchema,
  getPublishBlockers,
  hasUnsavedChanges,
  transformEndOfLifeValues,
  TAB_ERRORS,
  TAB_LABELS,
  TabsEnum,
  toNumberOrNull,
} from './utils'
import { usePrompt } from '../../hooks/usePrompt'
import { ContentTabs } from '../../components/ContentTabs'
import { RecordHeader } from '../../components/RecordHeader'
import DataQualitySection, {
  PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION,
  PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION,
} from '../../components/ProcessingActivitySections/DataQualitySection'
import ActivityRightPanel from '../../components/ActivityRightPanel'
import UnsavedChangesWarning from '../../components/UnsavedChangesWarning'
import { ActivityTypes, ROUTES } from '../../const'
import { CountryInfo } from './components/CountryInfo'
import { RecyclingRates } from './components/RecyclingRates'
import { WasteDisposalSplit } from './components/WasteDisposalSplit'
import './index.scss'

const TAB_ORDER = [
  TabsEnum.COUNTRY_INFO,
  TabsEnum.RECYCLING_RATES,
  TabsEnum.WASTE_DISPOSAL,
  TabsEnum.DATA_QUALITY,
]

// A publish rejection carries FluentValidation errors, not a ProblemDetails title: reading .title
// alone shows an empty notification for exactly the failures worth reading.
const toServerErrorMessage = (error: any): string => {
  const data = error.response?.data

  if (typeof data === 'string') {
    return data
  }
  if (Array.isArray(data)) {
    return data
      .map((item: any) => item.errorMessage ?? item.ErrorMessage)
      .filter(Boolean)
      .join('\n')
  }
  if (data?.errors && typeof data.errors !== 'string') {
    return Object.values(data.errors).flat().join('\n')
  }
  return data?.title ?? data?.errors ?? error.message ?? 'Something went wrong.'
}

const EndOfLife: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { loading, endOfLife, configuration } = useAppSelector(state => state.endOfLifeSlice)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [errors, setErrors] = useState<[any, string[]][]>()
  const [showPublished, setShowPublished] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const itemId = searchParams.get('itemId') || ''

  useEffect((): ReturnType<EffectCallback> => {
    if (itemId === '') {
      navigate('/not-found')
      return
    }
    dispatch(getConfiguration())
    if (itemId !== 'new') {
      dispatch(fetchEndOfLife(itemId, () => navigate(ROUTES.endOfLife)))
    }
    return () => {
      dispatch(setDefaultEndOfLife())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    initialValues: { ...endOfLife },
    validationSchema: formSchema,
    onSubmit: async (values, { setSubmitting }) => {
      // Nothing to publish until the record exists.
      if (itemId === 'new') {
        setSubmitting(false)
        return
      }

      const isPublishing = endOfLife.state === 0

      if (isPublishing) {
        // Same conditions PublishEndOfLifeValidator enforces, checked before the request so the
        // page names what is missing instead of surfacing a 400 the user has to decode.
        const blockers = getPublishBlockers(values)

        if (blockers.length > 0) {
          setShowError(blockers.join('\n'))
          setSelectedTab(TAB_ORDER.indexOf(blockerTab(blockers)))
          setSubmitting(false)
          return
        }
      }

      try {
        if (isPublishing) {
          await publishEndOfLife(itemId)
          dispatch(handlePublishStatus(1))
        } else {
          await unPublishEndOfLife(itemId)
          dispatch(handlePublishStatus(0))
        }
        setShowPublished(true)
        setTimeout(() => setShowPublished(false), 4000)
      } catch (error: any) {
        setShowError(toServerErrorMessage(error))
      } finally {
        // Formik leaves isSubmitting true for a handler that does not settle it, which would
        // disable Save for the rest of the page's life.
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    formik.setValues({ ...endOfLife })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endOfLife])

  const getTabErrors = useCallback(
    (tab: TabsEnum) =>
      TAB_ERRORS[tab].some(value => get(formik.touched, value) && !!get(formik.errors, value)),
    [formik.touched, formik.errors]
  )

  const tabs = TAB_ORDER.map(tab => ({
    label: TAB_LABELS[tab],
    hasErrors: getTabErrors(tab),
  }))

  // Changing the taxonomy level invalidates the selected product: the product list is the taxonomy
  // at that level, and a pair that disagrees is rejected by the BE validator.
  const handleTaxonomyLevelChange = useCallback(
    (e: React.ChangeEvent<Element>) => {
      formik.setValues({
        ...formik.values,
        productTaxonomyLevel: toNumberOrNull((e.target as HTMLSelectElement).value),
        productId: null,
      })
    },
    [formik]
  )

  const handleDataQualityFieldChange = useCallback(e => formik.handleChange(e), [formik])

  const handleCancel = useCallback(() => navigate(ROUTES.endOfLife), [navigate])

  const handlePublish = useCallback(async () => {
    const validationErrors = await formik.validateForm()
    const invalidTab = TAB_ORDER.find(tab => TAB_ERRORS[tab].some(key => key in validationErrors))
    if (invalidTab) {
      formik.setTouched(Object.fromEntries(Object.keys(validationErrors).map(key => [key, true])))
      setSelectedTab(TAB_ORDER.indexOf(invalidTab))
      setShowError('Please correct the highlighted fields before publishing.')
      return
    }
    formik.submitForm()
  }, [formik])

  const saveDraft = useCallback(
    async e => {
      e.preventDefault()
      if (formik.isSubmitting) {
        return
      }
      formik.setSubmitting(true)
      setErrors(undefined)
      setShowError('')

      const { values } = formik
      const validationErrors = await formik.validateForm()
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(Object.fromEntries(Object.keys(validationErrors).map(key => [key, true])))
        const invalidTab = TAB_ORDER.find(tab =>
          TAB_ERRORS[tab].some(key => key in validationErrors)
        )
        if (invalidTab) setSelectedTab(TAB_ORDER.indexOf(invalidTab))
        setShowError('Please correct the highlighted fields before saving.')
        formik.setSubmitting(false)
        return
      }
      const payload = transformEndOfLifeValues(values, configuration)

      try {
        if (itemId === 'new') {
          await createEndOfLife(payload)
          navigate(ROUTES.endOfLife)
        } else {
          await updateEndOfLife(itemId, payload)
          dispatch(fetchEndOfLife(itemId, () => navigate(ROUTES.endOfLife)))
          setShowNotification(true)
          setTimeout(() => setShowNotification(false), 5000)
        }
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          const data = error.response?.data
          setShowError(
            typeof data === 'string'
              ? data
              : typeof data?.errors === 'string'
              ? data.errors
              : Object.values(data?.errors ?? {})
                  .toString()
                  .replace(/!,/g, '!\n')
          )
          setTimeout(() => setShowError(''), 5000)
          if (data?.errors && typeof data.errors !== 'string') {
            setErrors(Object.entries(data.errors) as [any, string[]][])
          }
        }
      } finally {
        formik.setSubmitting(false)
      }
    },
    [formik, itemId, configuration, dispatch, navigate]
  )

  const isDirty = useMemo(
    () => hasUnsavedChanges(endOfLife, formik.values),
    [endOfLife, formik.values]
  )

  usePrompt('There are unsaved changes, do you want to leave?', itemId !== 'new' ? isDirty : false)

  if (loading === true && itemId !== 'new') {
    return (
      <div className="overlay">
        <LottieLoader
          lottieType="butterflyLottie"
          style={{ height: '10.25rem', width: '10.25rem' }}
        />
      </div>
    )
  }

  return (
    <>
      <div className="piece content end-of-life-item">
        {showError !== '' && (
          <div className={'piece-notification z-dialog'}>
            <Notification type="error" label="Oops:" description={showError} />
          </div>
        )}
        {showPublished && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}
        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              {
                path: ROUTES.endOfLife,
                title: 'End of Life Table',
              },
              {
                path: `${ROUTES.endOfLifeItem}?itemId=${itemId}`,
                title: 'End of Life Item',
              },
            ]}
          />
        </div>
        {itemId !== 'new' && (
          <RecordHeader
            label={endOfLife.code}
            isPublished={endOfLife.state === 1}
            datePublished={endOfLife.lastPublishedDate}
          />
        )}

        {/* Outside the form on purpose: a tab button inside it is an implicit submit button, so
            switching tabs would fire the publish/unpublish submit handler. */}
        <ContentTabs items={tabs} selectedIndex={selectedTab} onChange={setSelectedTab} />

        <form onSubmit={formik.handleSubmit} className="piece-content">
          {TAB_ORDER[selectedTab] === TabsEnum.COUNTRY_INFO && (
            <CountryInfo
              formik={formik}
              configuration={configuration}
              code={endOfLife.code}
              onTaxonomyLevelChange={handleTaxonomyLevelChange}
            />
          )}
          {TAB_ORDER[selectedTab] === TabsEnum.RECYCLING_RATES && (
            <RecyclingRates formik={formik} configuration={configuration} />
          )}
          {TAB_ORDER[selectedTab] === TabsEnum.WASTE_DISPOSAL && (
            <WasteDisposalSplit formik={formik} />
          )}
          {TAB_ORDER[selectedTab] === TabsEnum.DATA_QUALITY && (
            <DataQualitySection
              formik={formik}
              configuration={configuration}
              process={endOfLife}
              onFieldChange={handleDataQualityFieldChange}
              showCompletenessScore={false}
              referenceTechnologyTypeDescription={PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION}
              referenceTechnologyProxyContributionDescription={
                PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION
              }
            />
          )}

          {errors && (
            <ul className="piece-errors">
              {errors.map(([key, value]) =>
                (value as any).map(err => (
                  <li key={key + err}>
                    <b>{key}:</b> {err}
                  </li>
                ))
              )}
            </ul>
          )}

          <div className="end-of-life-item__actions">
            <UnsavedChangesWarning isDirty={isDirty} />
            <Button variant="secondary" className="w-32" size="md" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-32"
              size="md"
              onClick={saveDraft}
              disabled={formik.isSubmitting}
            >
              Save Draft
            </Button>
            {sessionStorage.getItem('permission') === 'true' && itemId !== 'new' && (
              <Button
                variant="tertiary"
                className="w-32"
                size="md"
                type="button"
                onClick={handlePublish}
                disabled={formik.isSubmitting || isDirty}
              >
                {endOfLife.state === 0 ? 'Publish' : 'Unpublish'}
              </Button>
            )}
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={itemId}
        activityType={ActivityTypes.EndOfLife}
        auditCreatedBy={endOfLife.researcherName}
        auditLastUpdatedBy={endOfLife.lastUpdatedName}
        companyId={formik.values.companyId}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
      />
    </>
  )
}

export default EndOfLife
